import CsInstance from "../../../state/CsInstance";

// CodeMirror
import {basicSetup} from "codemirror";
import {EditorView, keymap} from "@codemirror/view";
import {indentWithTab} from "@codemirror/commands";
import {Compartment, Extension} from "@codemirror/state";
import {indentUnit} from "@codemirror/language";
import {java} from "@codemirror/lang-java";
import {python} from "@codemirror/lang-python";
import {search} from "@codemirror/search";
import readOnlyRangesExtension from "./snippetsExtension";
import indentExtension from "./indentExtension";
import linterExtension from "./linterExtension";
import hoverExtension from "./hoverExtension";
import handleEditorChangeExtension from "./handleEditorChangeExtension";
import autocompletionExtension from "./autocompletionExtension";
import {createCodeMirrorTheme} from "./createThemeExtension";
import {indentationMarkers} from "@replit/codemirror-indentation-markers";

const DEFAULT_TAB_SIZE = 4;

export type CodeMirrorCompartments = {
	editableCompartment: Compartment;
	fontSizeCompartment: Compartment;
	themeCompartment: Compartment;
};

/**
 * Get the extensions to use in the CodeMirror editor.
 *
 * @param csInstance The CS instance.  The app's state determines many features
 *     of the editor, such as editability, which extensions to include, and
 *     which font size to use.
 * @returns An object with the following properties:
 *		- extensions: An array of extensions to apply to the CodeMirror editor.
 *		- compartments: An object of compartments for updating certain extensions.
 */
const getExtensions = (
	csInstance: CsInstance,
	compartments: CodeMirrorCompartments
): Extension[] => {
	const {featureFlagStore, codeStore, compileStore, uiStore, userInputStore} =
		csInstance;

	// First, create the compartments

	// Can only edit if the editor is focused and there are files open
	const editable =
		!userInputStore.isTheaterFocused && codeStore.filesOpen.length > 0;

	const fontSizeTheme = EditorView.theme({
		"&": {fontSize: uiStore.editorFontSize + "rem"},
	});

	const theme = createCodeMirrorTheme(uiStore);

	// Create event listeners

	const contextMenuFunction = (e: MouseEvent) => {
		// Prevent the default context menu from appearing when right-clicking on the custom menu
		if (document.getElementById("contextMenu")?.contains(e.target as Node)) {
			e.preventDefault();
		}
		// Handle right-clicking on the editor to open the context menu.
		if (document.getElementById("editor")?.contains(e.target as Node)) {
			uiStore.contextMenuPosition = {x: e.clientX, y: e.clientY};
			uiStore.showContextMenu = true;
			e.preventDefault();
		} else {
			uiStore.showContextMenu = false;
		}
	};

	const onKeyDownHandler = (e: KeyboardEvent) => {
		uiStore.onKeyDownHandler(e, userInputStore.isEditorFocused);
	};

	// Then, use compartments to create the extensions

	let extensions = [
		featureFlagStore.usePython ? python() : java(), // Syntax highlighting based on feature flag
		search({top: true}),
		compartments.editableCompartment.of(EditorView.editable.of(editable)),
		basicSetup,
		keymap.of([indentWithTab]),
		indentUnit.of(" ".repeat(DEFAULT_TAB_SIZE)),
		compartments.fontSizeCompartment.of(fontSizeTheme),
		compartments.themeCompartment.of(theme),
		indentExtension(),
		hoverExtension(codeStore),
		handleEditorChangeExtension(codeStore),
		autocompletionExtension(codeStore),
		linterExtension(codeStore, compileStore),
		EditorView.domEventHandlers({
			contextmenu: contextMenuFunction,
			keydown: onKeyDownHandler,
		}),
		indentationMarkers({
			colors: {
				light: "#e0e0e0",
				activeLight: "#b0b0b0",
			},
		}),
	];
	if (featureFlagStore.useReadOnlyRanges) {
		extensions = extensions.concat(readOnlyRangesExtension());
	}

	return extensions;
};

export default getExtensions;
